import { useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight, MessageCircle, Mail, MapPin, Clock, UploadCloud, X, FileText,
  CheckCircle2, ShieldCheck, Loader2, Phone, Search,
} from "lucide-react";
import { Reveal } from "../components/Reveal";
import { PageHero, usePageTitle } from "../components/Shared";
import { WHATSAPP_URL, WHATSAPP_NUMBER, CONTACT_EMAIL, PHONE_DISPLAY, PHONE_TEL } from "../data/content";
import { useLang, useContent } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_SIZE = 10 * 1024 * 1024;

const COPY = {
  fr: {
    meta: "Demander une démarche",
    eyebrow: "Contact",
    h1a: "Demander",
    h1b: "une démarche.",
    sub: "Remplissez ce formulaire en 2 minutes. Un conseiller DOC TOGO vous recontacte sous 24h avec les informations, les conditions et un devis clair.",
    catLabel: "Catégorie du besoin *",
    catPlaceholder: "Sélectionnez une catégorie",
    svcLabel: "Démarche spécifique",
    svcPlaceholder: "Sélectionnez une démarche",
    svcFirst: "Choisissez d'abord une catégorie",
    nom: "Nom complet *",
    nomPh: "Ex : Awa Mensah",
    email: "Adresse email *",
    tel: "Téléphone / WhatsApp *",
    telPh: "+228, +33, +1…",
    pays: "Pays de résidence *",
    paysPh: "Ex : France, Togo, Canada…",
    profil: "Votre profil *",
    profilPh: "Sélectionnez votre profil",
    message: "Précisions sur votre besoin",
    messagePh: "Décrivez votre situation, vos délais souhaités, toute information utile…",
    docs: "Documents (optionnel — PDF, JPG, PNG · max 5 × 10 Mo)",
    drop: "Glissez vos fichiers ici ou cliquez pour parcourir",
    dropHint: "Pièce d'identité, ancien document, justificatif…",
    consent: "J'accepte que mes informations et documents soient utilisés uniquement dans le cadre de ma demande, conformément à la politique de confidentialité de DOC TOGO. *",
    submit: "Envoyer ma demande",
    sending: "Envoi en cours…",
    errConsent: "Veuillez accepter la politique de confidentialité",
    errMax: "Maximum 5 fichiers",
    errSize: (n) => `${n} dépasse 10 Mo`,
    errGeneric: "Une erreur est survenue. Réessayez ou contactez-nous sur WhatsApp.",
    successTitle: "Demande envoyée avec succès",
    successText: (prenom, email) => <>Merci {prenom} ! Votre demande a bien été transmise à nos équipes. Un conseiller DOC TOGO vous recontactera sous 24h ouvrées à l'adresse <strong className="text-white">{email}</strong>.</>,
    successRef: "Votre référence de dossier",
    successWhatsapp: (ref) => `Bonjour DOC TOGO, je viens d'envoyer la demande ${ref}.`,
    successWaLabel: "Suivre sur WhatsApp",
    successTrack: "Suivre mon dossier",
    successHome: "Retour à l'accueil",
    sideTitle: "Contact direct",
    sideWa: "WhatsApp",
    sideCalls: "Appels",
    sideEmail: "Email",
    sideAddr: "Adresse",
    sideHours: "Horaires",
    hours: "Lun – Sam · 8h – 18h (GMT)",
    secTitle: "Transmission sécurisée",
    secText: "Vos documents sont transmis via une connexion chiffrée et stockés en toute confidentialité. Ils ne servent qu'à votre démarche — jamais à autre chose.",
  },
  en: {
    meta: "Start a request",
    eyebrow: "Contact",
    h1a: "Start",
    h1b: "your request.",
    sub: "Fill in this form in 2 minutes. A DOC TOGO advisor contacts you within 24h with information, terms and a clear quote.",
    catLabel: "Need category *",
    catPlaceholder: "Select a category",
    svcLabel: "Specific procedure",
    svcPlaceholder: "Select a procedure",
    svcFirst: "Choose a category first",
    nom: "Full name *",
    nomPh: "E.g. Awa Mensah",
    email: "Email address *",
    tel: "Phone / WhatsApp *",
    telPh: "+228, +33, +1…",
    pays: "Country of residence *",
    paysPh: "E.g. France, Togo, Canada…",
    profil: "Your profile *",
    profilPh: "Select your profile",
    message: "Details about your need",
    messagePh: "Describe your situation, desired timelines, any useful information…",
    docs: "Documents (optional — PDF, JPG, PNG · max 5 × 10 MB)",
    drop: "Drop your files here or click to browse",
    dropHint: "ID card, previous document, proof…",
    consent: "I agree that my information and documents will be used solely for my request, in accordance with DOC TOGO's privacy policy. *",
    submit: "Send my request",
    sending: "Sending…",
    errConsent: "Please accept the privacy policy",
    errMax: "Maximum 5 files",
    errSize: (n) => `${n} exceeds 10 MB`,
    errGeneric: "Something went wrong. Try again or contact us on WhatsApp.",
    successTitle: "Request sent successfully",
    successText: (prenom, email) => <>Thank you {prenom}! Your request has been sent to our teams. A DOC TOGO advisor will contact you within 24 business hours at <strong className="text-white">{email}</strong>.</>,
    successRef: "Your case reference",
    successWhatsapp: (ref) => `Hello DOC TOGO, I just submitted request ${ref}.`,
    successWaLabel: "Track on WhatsApp",
    successTrack: "Track my case",
    successHome: "Back to home",
    sideTitle: "Direct contact",
    sideWa: "WhatsApp",
    sideCalls: "Calls",
    sideEmail: "Email",
    sideAddr: "Address",
    sideHours: "Hours",
    hours: "Mon – Sat · 8am – 6pm (GMT)",
    secTitle: "Secure transmission",
    secText: "Your documents are transmitted over an encrypted connection and stored confidentially. They are used only for your procedure — never for anything else.",
  },
};

const inputCls =
  "w-full rounded-xl border border-brand/15 bg-white px-4 py-3 text-base sm:text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-[border-color,box-shadow] duration-300 focus:border-brand focus:ring-2 focus:ring-brand/15";

const Label = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
    {children}
  </label>
);

export default function Contact() {
  const { lang } = useLang();
  const { FORM_CATEGORIES, FORM_SERVICES_FOR, PROFILS } = useContent();
  const c = COPY[lang];
  usePageTitle(c.meta);
  const [searchParams] = useSearchParams();
  const initialCat = FORM_CATEGORIES.some((cat) => cat.value === searchParams.get("categorie")) ? searchParams.get("categorie") : "";
  const [form, setForm] = useState({
    nom: "", email: "", telephone: "", pays: "", profil: "", categorie: initialCat, service: "", message: "",
  });
  const [files, setFiles] = useState([]);
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reference, setReference] = useState(null);
  const fileInput = useRef(null);

  const set = (k) => (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, [k]: v, ...(k === "categorie" ? { service: "" } : {}) }));
  };

  const addFiles = (list) => {
    const incoming = Array.from(list || []);
    const merged = [...files];
    for (const f of incoming) {
      if (merged.length >= 5) { toast.error(c.errMax); break; }
      if (f.size > MAX_SIZE) { toast.error(c.errSize(f.name)); continue; }
      merged.push(f);
    }
    setFiles(merged);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error(c.errConsent); return; }
    setLoading(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      files.forEach((f) => data.append("fichiers", f));
      const res = await axios.post(`${API}/demandes`, data, { timeout: 90000 });
      setReference(res.data.reference);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : c.errGeneric);
    } finally {
      setLoading(false);
    }
  };

  if (reference) {
    return (
      <div data-testid="contact-page">
        <section className="bg-brand-deep px-6 pb-24 pt-40 text-white">
          <div className="mx-auto max-w-2xl text-center">
            <Reveal>
              <CheckCircle2 className="mx-auto h-16 w-16 text-gold" />
              <h1 className="mt-6 font-display text-3xl font-extrabold sm:text-4xl">{c.successTitle}</h1>
              <p className="mt-4 text-base leading-relaxed text-white/60">
                {c.successText(form.nom.split(" ")[0], form.email)}
              </p>
              <div data-testid="success-reference" className="mx-auto mt-8 inline-block rounded-2xl border border-gold/30 bg-white/5 px-8 py-5 backdrop-blur-md">
                <div className="text-xs uppercase tracking-[0.25em] text-white/50">{c.successRef}</div>
                <div className="mt-1 font-mono text-2xl font-bold text-gold">{reference}</div>
              </div>
              <div className="mt-10 flex flex-col flex-wrap justify-center gap-4 sm:flex-row">
                <Link
                  to={`/suivi?ref=${reference}`}
                  data-testid="success-suivi-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-gold px-7 py-3.5 font-display font-bold text-brand-deep transition-[background-color,transform] duration-300 hover:bg-gold-warm hover:-translate-y-0.5"
                >
                  <Search className="h-5 w-5" /> {c.successTrack}
                </Link>
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(c.successWhatsapp(reference))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="success-whatsapp-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-7 py-3.5 font-display font-bold text-white transition-[background-color,transform] duration-300 hover:bg-[#1fb857] hover:-translate-y-0.5"
                >
                  <MessageCircle className="h-5 w-5" /> {c.successWaLabel}
                </a>
                <Link
                  to="/"
                  data-testid="success-home-btn"
                  className="inline-flex items-center justify-center rounded-full border border-white/25 px-7 py-3.5 font-display font-bold text-white transition-[background-color] duration-300 hover:bg-white/10"
                >
                  {c.successHome}
                </Link>
              </div>
            </Reveal>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="contact-page">
      <PageHero eyebrow={c.eyebrow} lines={[<>{c.h1a}</>, <>{c.h1b}</>]} sub={c.sub} />
      <section className="bg-ivory py-20 sm:py-24">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 lg:grid-cols-12">
          <Reveal className="lg:col-span-8">
            <form
              onSubmit={submit}
              data-testid="demande-form"
              className="rounded-3xl border border-brand/10 bg-white p-7 shadow-sm sm:p-10"
            >
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <Label htmlFor="categorie">{c.catLabel}</Label>
                  <select id="categorie" data-testid="form-categorie" required value={form.categorie} onChange={set("categorie")} className={inputCls}>
                    <option value="" disabled>{c.catPlaceholder}</option>
                    {FORM_CATEGORIES.map((cat) => <option key={cat.value} value={cat.value}>{cat.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label htmlFor="service">{c.svcLabel}</Label>
                  <select id="service" data-testid="form-service" value={form.service} onChange={set("service")} className={inputCls} disabled={!form.categorie}>
                    <option value="">{form.categorie ? c.svcPlaceholder : c.svcFirst}</option>
                    {form.categorie && FORM_SERVICES_FOR(form.categorie).map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label htmlFor="nom">{c.nom}</Label>
                  <input id="nom" data-testid="form-nom" required minLength={2} maxLength={120} value={form.nom} onChange={set("nom")} placeholder={c.nomPh} autoComplete="name" className={inputCls} />
                </div>
                <div>
                  <Label htmlFor="email">{c.email}</Label>
                  <input id="email" type="email" data-testid="form-email" required value={form.email} onChange={set("email")} placeholder="vous@exemple.com" autoComplete="email" inputMode="email" className={inputCls} />
                </div>
                <div>
                  <Label htmlFor="telephone">{c.tel}</Label>
                  <input id="telephone" type="tel" data-testid="form-telephone" required minLength={6} maxLength={25} value={form.telephone} onChange={set("telephone")} placeholder={c.telPh} autoComplete="tel" inputMode="tel" className={inputCls} />
                </div>
                <div>
                  <Label htmlFor="pays">{c.pays}</Label>
                  <input id="pays" data-testid="form-pays" required minLength={2} maxLength={80} value={form.pays} onChange={set("pays")} placeholder={c.paysPh} autoComplete="country-name" className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="profil">{c.profil}</Label>
                  <select id="profil" data-testid="form-profil" required value={form.profil} onChange={set("profil")} className={inputCls}>
                    <option value="" disabled>{c.profilPh}</option>
                    {PROFILS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="message">{c.message}</Label>
                  <textarea id="message" data-testid="form-message" rows={4} maxLength={3000} value={form.message} onChange={set("message")} placeholder={c.messagePh} className={`${inputCls} resize-y`} />
                </div>
                <div className="sm:col-span-2">
                  <Label>{c.docs}</Label>
                  <div
                    data-testid="form-dropzone"
                    onClick={() => fileInput.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
                    className="cursor-pointer rounded-2xl border-2 border-dashed border-brand/25 bg-brand-light/40 px-6 py-8 text-center transition-[border-color,background-color] duration-300 hover:border-brand hover:bg-brand-light"
                  >
                    <UploadCloud className="mx-auto h-8 w-8 text-brand" />
                    <p className="mt-2 text-sm font-semibold text-slate-700">{c.drop}</p>
                    <p className="mt-1 text-xs text-slate-400">{c.dropHint}</p>
                    <input
                      ref={fileInput}
                      type="file"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                      data-testid="form-file-input"
                      className="hidden"
                      onChange={(e) => addFiles(e.target.files)}
                    />
                  </div>
                  {files.length > 0 && (
                    <ul className="mt-4 space-y-2" data-testid="form-file-list">
                      {files.map((f, i) => (
                        <li key={`${f.name}-${i}`} className="flex items-center justify-between rounded-xl border border-brand/10 bg-ivory px-4 py-2.5 text-sm">
                          <span className="flex items-center gap-2.5 truncate text-slate-700">
                            <FileText className="h-4 w-4 shrink-0 text-brand" />
                            <span className="truncate">{f.name}</span>
                            <span className="shrink-0 text-xs text-slate-400">{(f.size / 1024 / 1024).toFixed(1)} Mo</span>
                          </span>
                          <button type="button" data-testid={`form-file-remove-${i}`} onClick={() => setFiles(files.filter((_, j) => j !== i))} className="ml-3 rounded-full p-1 text-slate-400 transition-colors hover:bg-crimson/10 hover:text-crimson" aria-label={`Retirer ${f.name}`}>
                            <X className="h-4 w-4" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <label className="flex cursor-pointer items-start gap-3 text-sm text-slate-600">
                    <input
                      type="checkbox"
                      data-testid="form-consent"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-brand/30 accent-[#0D482B]"
                    />
                    <span>{c.consent}</span>
                  </label>
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="form-submit-btn"
                className="group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand px-8 py-4 font-display text-base font-bold text-white transition-[background-color,transform] duration-300 hover:bg-brand-hover hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />}
                {loading ? c.sending : c.submit}
              </button>
            </form>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-4">
            <div className="sticky top-28 space-y-6">
              <div className="rounded-3xl bg-brand-deep p-8 text-white">
                <h3 className="font-display text-xl font-bold">{c.sideTitle}</h3>
                <ul className="mt-6 space-y-5 text-sm">
                  <li>
                    <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link" className="group flex items-start gap-3.5">
                      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#25D366]/15 text-[#25D366]"><MessageCircle className="h-5 w-5" /></span>
                      <span>
                        <span className="block font-semibold text-white transition-colors group-hover:text-gold">{c.sideWa}</span>
                        <span className="text-white/55">{PHONE_DISPLAY}</span>
                      </span>
                    </a>
                  </li>
                  <li>
                    <a href={PHONE_TEL} data-testid="contact-phone-link" className="group flex items-start gap-3.5">
                      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white/10 text-white"><Phone className="h-5 w-5" /></span>
                      <span>
                        <span className="block font-semibold text-white transition-colors group-hover:text-gold">{c.sideCalls}</span>
                        <span className="text-white/55">{PHONE_DISPLAY}</span>
                      </span>
                    </a>
                  </li>
                  <li>
                    <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-email-link" className="group flex items-start gap-3.5">
                      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gold/15 text-gold"><Mail className="h-5 w-5" /></span>
                      <span>
                        <span className="block font-semibold text-white transition-colors group-hover:text-gold">{c.sideEmail}</span>
                        <span className="text-white/55">{CONTACT_EMAIL}</span>
                      </span>
                    </a>
                  </li>
                  <li className="flex items-start gap-3.5">
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-crimson/20 text-red-400"><MapPin className="h-5 w-5" /></span>
                    <span>
                      <span className="block font-semibold text-white">{c.sideAddr}</span>
                      <span className="text-white/55">Lomé, Togo</span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3.5">
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white/10 text-white"><Clock className="h-5 w-5" /></span>
                    <span>
                      <span className="block font-semibold text-white">{c.sideHours}</span>
                      <span className="text-white/55">{c.hours}</span>
                    </span>
                  </li>
                </ul>
              </div>
              <div className="rounded-3xl border border-brand/10 bg-white p-8">
                <ShieldCheck className="h-8 w-8 text-brand" />
                <h3 className="mt-4 font-display text-lg font-bold text-slate-900">{c.secTitle}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{c.secText}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
